import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Cart, Product } from '../types';
import api from '../services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: !!localStorage.getItem('access_token'),
      loading: false,

      login: async (email: string, password: string) => {
        set({ loading: true });
        try {
          await api.login(email, password);
          const user = await api.getCurrentUser();
          set({ user, isAuthenticated: true, loading: false });
        } catch (error) {
          set({ loading: false });
          throw error;
        }
      },

      register: async (data: any) => {
        set({ loading: true });
        try {
          await api.register(data);
          set({ loading: false });
        } catch (error) {
          set({ loading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.logout();
        } catch (error) {
          console.error('Logout error:', error);
        }
        set({ user: null, isAuthenticated: false });
      },

      fetchUser: async () => {
        if (!localStorage.getItem('access_token')) {
          set({ isAuthenticated: false, user: null });
          return;
        }
        
        try {
          const user = await api.getCurrentUser();
          set({ user, isAuthenticated: true });
        } catch (error) {
          set({ user: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

interface CartState {
  cart: Cart | null;
  loading: boolean;
  fetchCart: () => Promise<void>;
  addToCart: (productId: number, quantity?: number, selectedSize?: string) => Promise<void>;
  updateQuantity: (itemId: number, quantity: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  clearCart: () => Promise<void>;
}

// Helper function to get guest cart from localStorage
const getGuestCart = (): Cart => {
  const guestCartJson = localStorage.getItem('guest_cart');
  if (guestCartJson) {
    try {
      return JSON.parse(guestCartJson);
    } catch {
      return { items: [], total: '0', subtotal: '0', discount: 0, tax: 0, items_count: 0 };
    }
  }
  return { items: [], total: '0', subtotal: '0', discount: 0, tax: 0, items_count: 0 };
};

// Helper function to save guest cart to localStorage
const saveGuestCart = (cart: Cart) => {
  localStorage.setItem('guest_cart', JSON.stringify(cart));
};

// Helper function to calculate guest cart totals
const calculateGuestCartTotals = (items: any[]) => {
  let subtotal = 0;
  items.forEach((item: any) => {
    subtotal += parseFloat(item.product?.price || 0) * item.quantity;
  });
  return {
    subtotal: subtotal.toFixed(2),
    total: subtotal.toFixed(2),
    items_count: items.length,
  };
};

export const useCartStore = create<CartState>((set, get) => ({
  cart: null,
  loading: false,

  fetchCart: async () => {
    const isAuthenticated = !!localStorage.getItem('access_token');
    
    if (!isAuthenticated) {
      // Load guest cart from localStorage
      const guestCart = getGuestCart();
      set({ cart: guestCart });
      return;
    }
    
    set({ loading: true });
    try {
      const cart = await api.getCart();
      set({ cart, loading: false });
    } catch (error) {
      set({ loading: false });
    }
  },

  addToCart: async (productId: number, quantity: number = 1, selectedSize?: string) => {
    const isAuthenticated = !!localStorage.getItem('access_token');
    
    if (isAuthenticated) {
      // Authenticated user - use API
      try {
        await api.addToCart(productId, quantity, selectedSize);
        await get().fetchCart();
      } catch (error) {
        throw error;
      }
    } else {
      // Guest user - save to local cart
      try {
        const guestCart = getGuestCart();
        
        // Fetch product details
        const product = await api.getProduct(productId);
        
        // Check if item already exists
        const existingItem = guestCart.items.find(
          (item: any) => item.product?.id === productId && item.selected_size === (selectedSize || '')
        );
        
        const itemPrice = parseFloat(product.price || '0');
        const itemTotal = (itemPrice * quantity).toFixed(2);
        
        if (existingItem) {
          existingItem.quantity += quantity;
          existingItem.total = (parseFloat(existingItem.total) + parseFloat(itemTotal)).toFixed(2);
          existingItem.subtotal = existingItem.total;
        } else {
          guestCart.items.push({
            id: Math.random(),
            product,
            quantity,
            selected_size: selectedSize || '',
            total: itemTotal,
            subtotal: itemTotal,
          });
        }
        
        // Recalculate totals
        const totals = calculateGuestCartTotals(guestCart.items);
        guestCart.total = totals.total;
        guestCart.subtotal = totals.subtotal;
        guestCart.items_count = totals.items_count;
        
        saveGuestCart(guestCart);
        set({ cart: guestCart });
      } catch (error) {
        throw error;
      }
    }
  },

  updateQuantity: async (itemId: number, quantity: number) => {
    const isAuthenticated = !!localStorage.getItem('access_token');
    
    if (isAuthenticated) {
      try {
        await api.updateCartItem(itemId, quantity);
        await get().fetchCart();
      } catch (error) {
        throw error;
      }
    } else {
      // Guest user - update local cart
      const guestCart = getGuestCart();
      const item = guestCart.items.find((item: any) => item.id === itemId);
      if (item) {
        item.quantity = quantity;
        const totals = calculateGuestCartTotals(guestCart.items);
        guestCart.total = totals.total;
        guestCart.subtotal = totals.subtotal;
        guestCart.items_count = totals.items_count;
        saveGuestCart(guestCart);
        set({ cart: guestCart });
      }
    }
  },

  removeItem: async (itemId: number) => {
    const isAuthenticated = !!localStorage.getItem('access_token');
    
    if (isAuthenticated) {
      try {
        await api.removeFromCart(itemId);
        await get().fetchCart();
      } catch (error) {
        throw error;
      }
    } else {
      // Guest user - remove from local cart
      const guestCart = getGuestCart();
      guestCart.items = guestCart.items.filter((item: any) => item.id !== itemId);
      const totals = calculateGuestCartTotals(guestCart.items);
      guestCart.total = totals.total;
      guestCart.subtotal = totals.subtotal;
      guestCart.items_count = totals.items_count;
      saveGuestCart(guestCart);
      set({ cart: guestCart });
    }
  },

  clearCart: async () => {
    const isAuthenticated = !!localStorage.getItem('access_token');
    
    if (isAuthenticated) {
      try {
        await api.clearCart();
        set({ cart: null });
      } catch (error) {
        throw error;
      }
    } else {
      // Guest user - clear local cart
      localStorage.removeItem('guest_cart');
      set({ cart: null });
    }
  },
}));

interface WishlistState {
  wishlist: number[];
  wishlistItems: any[];
  loading: boolean;
  fetchWishlist: () => Promise<void>;
  addToWishlist: (productId: number) => Promise<void>;
  removeFromWishlist: (productId: number) => Promise<void>;
  isInWishlist: (productId: number) => boolean;
}

export const useWishlistStore = create<WishlistState>((set, get) => ({
  wishlist: [],
  wishlistItems: [],
  loading: false,

  fetchWishlist: async () => {
    if (!localStorage.getItem('access_token')) return;
    
    set({ loading: true });
    try {
      const data = await api.getWishlist();
      console.log('Wishlist data from API:', data); // Debug log
      
      // Handle different response formats
      let items = [];
      if (Array.isArray(data)) {
        items = data;
      } else if (data && data.items) {
        items = data.items;
      } else if (data && Array.isArray(data.results)) {
        items = data.results;
      }
      
      // Extract product IDs
      const wishlist = items
        .map((item: any) => {
          if (typeof item === 'number') return item;
          if (typeof item.product === 'number') return item.product;
          if (item.product && item.product.id) return item.product.id;
          return null;
        })
        .filter(Boolean);
      
      console.log('Processed wishlist IDs:', wishlist); // Debug log
      set({ wishlist, wishlistItems: items, loading: false });
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      set({ wishlist: [], loading: false });
    }
  },

  addToWishlist: async (productId: number) => {
    try {
      await api.addToWishlist(productId);
      set((state) => ({ wishlist: [...state.wishlist, productId] }));
      // Re-fetch to ensure sync with backend
      await get().fetchWishlist();
    } catch (error) {
      throw error;
    }
  },

  removeFromWishlist: async (productId: number) => {
    try {
      await api.removeFromWishlist(productId);
      set((state) => ({
        wishlist: state.wishlist.filter((id) => id !== productId),
      }));
      // Re-fetch to ensure sync with backend
      await get().fetchWishlist();
    } catch (error) {
      throw error;
    }
  },

  isInWishlist: (productId: number) => {
    return get().wishlist.includes(productId);
  },
}));
